// The calendar, below Bootstrap's `md` breakpoint.
//
// Not a shrunk grid -- a replacement. Seven columns on a phone leaves about
// 50px per day, which is not enough for a bar to name the place you are that
// day; and a horizontally scrolling calendar is worse than no calendar. So the
// same month's trips are listed instead, in the order they happen, each
// carrying the color it has on the grid and the same per-day destinations its
// bar would.
//
// It doubles as the best screen-reader rendering of a month, which is why the
// grid doesn't also carry a visually-hidden linear summary.

import { tripGroupDateRange, tripGroupSummary } from '../../fleet-shared/TripGroups.mjs';
import { legStops, dayIndexLabel } from '../../fleet-shared/TripCalendar.mjs';
import { resolvePlaceName } from '../../fleet-shared/PlaceFields.mjs';
import { calendarColorVar } from './tripCalendarColors.mjs';

// Long enough for a fortnight's cruising; a 30-leg delivery would otherwise be
// a paragraph on a phone.
const STOP_LIMIT = 6;

// The grid's day columns, said as a line: "Aug 29 Nanaimo · Aug 30 Hornby
// Island · ...". The same per-leg destinations the bars carry, which is the
// whole reason this view exists rather than a squeezed grid.
//
// Dates come off the day INDEX, not the stored instant, so this line can't
// disagree with the column the desktop grid draws. No year -- the date range on
// the line above already said it, eight times over would be noise.
//
// The WHOLE trip, not just the part inside the displayed month: the row is
// about the trip, and its date range says so too.
function stopLine(group, placeIndex) {
  const named = legStops(group).filter((stop) => stop.name);
  if (!named.length) return null;
  const shown = named
    .slice(0, STOP_LIMIT)
    .map((stop) => `${dayIndexLabel(stop.dayIndex)} ${resolvePlaceName(stop.name, placeIndex)}`)
    .join(' · ');
  const more = named.length - STOP_LIMIT;
  return more > 0 ? `${shown} · +${more} more` : shown;
}

const CalendarAgenda = ({ calendar, colorIndexFor, placeIndex, onSelectGroup }) => {
  if (!calendar) return null;

  if (calendar.visible.length === 0) {
    return (
      <p className="text-center text-body-secondary mb-0">
        Nothing on the calendar in {calendar.monthLabel}.
      </p>
    );
  }

  return (
    <div className="trip-cal-agenda">
      {calendar.visible.map((group) => {
        // Beside the summary, not instead of it: that line carries the trip's
        // distance and is shared with the trips list.
        const stops = stopLine(group, placeIndex);
        return (
          <button
            key={group.key}
            type="button"
            className="trip-cal-agenda__row"
            style={{ '--trip-color': calendarColorVar(colorIndexFor?.(group.key) ?? 0) }}
            onClick={() => onSelectGroup?.(group)}
          >
            <span className="trip-cal-swatch mt-1" aria-hidden="true" />
            <span className="flex-grow-1 min-w-0">
              <span className="d-block trip-cal-agenda__name">{group.name}</span>
              <span className="d-block trip-cal-agenda__meta">{tripGroupDateRange(group)}</span>
              <span className="d-block trip-cal-agenda__meta">
                {tripGroupSummary(group, { placeIndex })}
              </span>
              {stops && <span className="d-block trip-cal-agenda__meta">{stops}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default CalendarAgenda;
